import type { ModuleDataset, TrendPoint } from "@/types/analytics";

const monthLabels = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun"];

function createTrend(seed: number): TrendPoint[] {
  return monthLabels.map((period, index) => ({
    period,
    primary: seed + index * 8 + (index % 2 === 0 ? 4 : 9),
    secondary: seed - 10 + index * 5 + (index % 3 === 0 ? 8 : 2),
  }));
}

const datasets: Record<string, ModuleDataset> = {
  dashboard: {
    overview:
      "PRIMA menggabungkan sinyal fiskal, regional, dan media ke dalam satu tampilan yang ramah eksekutif menggunakan data mock.",
    metrics: [
      {
        label: "Total TKD",
        value: 1523400000000,
        unit: "IDR",
        delta: 5.8,
        tone: "fiscal",
        helperText: "Dibandingkan snapshot periode sebelumnya",
      },
      {
        label: "Total Dana Desa",
        value: 842500000000,
        unit: "IDR",
        delta: 3.2,
        tone: "success",
        helperText: "Konteks pagu tahunan dengan tampilan realisasi bulanan",
      },
      {
        label: "Total Satker",
        value: 24821,
        delta: 1.1,
        tone: "intelligence",
        helperText: "Entitas organisasi yang sudah dimuat",
      },
      {
        label: "Total Kabupaten/Kota",
        value: 514,
        delta: 0,
        tone: "warning",
        helperText: "Anggota wilayah otoritatif",
      },
    ],
    trend: createTrend(68),
    rankings: [
      { name: "Jawa Barat", region: "Provinsi", value: 94, quality: "Complete" },
      { name: "Jawa Timur", region: "Provinsi", value: 91, quality: "Complete" },
      { name: "Sumatera Utara", region: "Provinsi", value: 88, quality: "Partial" },
      { name: "Sulawesi Selatan", region: "Provinsi", value: 84, quality: "Complete" },
    ],
    alerts: [
      {
        title: "Cakupan referensi APBN perlu perhatian",
        body: "Referensi BA yang belum cocok masih terlihat pada drilldown kelembagaan.",
        severity: "High",
        module: "APBN",
      },
      {
        title: "Tren bulanan Dana Desa diperbarui",
        body: "Payload bulan terbaru telah disegarkan dari layanan mock.",
        severity: "Low",
        module: "Dana Desa",
      },
      {
        title: "Cache ringkasan media mendekati kedaluwarsa",
        body: "Penyegaran akan dibutuhkan untuk tampilan narasi wilayah-topik.",
        severity: "Medium",
        module: "Media",
      },
    ],
    advisor: {
      headline: "Posisi strategis tetap stabil dengan beberapa titik pantau terfokus.",
      overview:
        "Sinyal mock menunjukkan momentum fiskal yang kuat, tetapi kualitas operasional harus tetap terlihat pada alur kerja APBN dan tata kelola.",
      bullets: [
        "Garis tren TKD dan Dana Desa tetap positif pada snapshot saat ini.",
        "Drilldown organisasi perlu mempertahankan badge kualitas referensi.",
        "Lapisan narasi regional harus tetap menjadi pelengkap data fiskal sumber.",
      ],
      caution:
        "Kartu advisor ini hanya menggunakan data mock dan disiapkan untuk integrasi API di tahap berikutnya.",
    },
    filters: ["Tahun", "Bulan", "Provinsi", "Regional", "Cakupan"],
    endpoints: [
      "/api/v1/dashboard/overview",
      "/api/v1/dashboard/kpis",
      "/api/v1/dashboard/rankings",
      "/api/v1/ai/summary/executive",
    ],
  },
  fiscal: {
    overview:
      "Ruang kerja fiskal terpadu untuk membandingkan transfer nasional, realisasi, dan kinerja organisasi.",
    metrics: [
      {
        label: "Sinyal Fiskal",
        value: 88,
        unit: "Indeks",
        delta: 2.4,
        tone: "fiscal",
        helperText: "Skor fiskal gabungan",
      },
      {
        label: "Tingkat Realisasi",
        value: 76.4,
        unit: "%",
        delta: 1.6,
        tone: "success",
        helperText: "Efisiensi realisasi mock",
      },
      {
        label: "Tanda Risiko",
        value: 42,
        delta: -8.2,
        tone: "warning",
        helperText: "Isu terlihat yang perlu ditinjau",
      },
      {
        label: "Entitas Aktif",
        value: 631,
        delta: 3.7,
        tone: "intelligence",
        helperText: "Unit pelaporan fiskal dalam cakupan",
      },
    ],
    trend: createTrend(54),
    rankings: [
      { name: "Kanwil Jawa Barat", region: "Kanwil", value: 92, quality: "Complete" },
      { name: "Kanwil DKI Jakarta", region: "Kanwil", value: 89, quality: "Complete" },
      { name: "Kanwil Aceh", region: "Kanwil", value: 81, quality: "Flagged" },
    ],
    alerts: [
      {
        title: "Catatan referensi telah diteruskan",
        body: "Join organisasi tetap aman untuk V1 dan dinyatakan secara eksplisit.",
        severity: "Medium",
        module: "Fiskal",
      },
    ],
    advisor: {
      headline: "Kinerja fiskal membaik tetapi masih belum merata.",
      overview:
        "Kombinasi mock menunjukkan realisasi di level kanwil lebih baik dibanding potongan satker yang lebih dalam.",
      bullets: [
        "Gunakan API ringkasan fiskal untuk tampilan eksekutif.",
        "Pisahkan filter teritorial dan organisasi.",
        "Tampilkan referensi bermasalah di seluruh tabel fiskal.",
      ],
      caution: "Semua grafik dan KPI masih menggunakan data mock.",
    },
    filters: ["Tahun", "Bulan", "Kanwil", "KPPN", "BA", "Provinsi"],
    endpoints: [
      "/api/v1/dashboard/fiscal/overview",
      "/api/v1/dashboard/fiscal/trends",
      "/api/v1/dashboard/fiscal/quality",
    ],
  },
  tkd: {
    overview: "Pemantauan transfer ke daerah dengan tampilan pagu, realisasi, dan distribusi.",
    metrics: [
      {
        label: "Total Pagu",
        value: 962,
        unit: "B",
        delta: 4.2,
        tone: "fiscal",
        helperText: "Skala anggaran mock",
      },
      {
        label: "Total Realisasi",
        value: 741,
        unit: "B",
        delta: 6.1,
        tone: "success",
        helperText: "Nilai realisasi mock",
      },
      {
        label: "Jenis Transfer",
        value: 7,
        tone: "intelligence",
        helperText: "Jumlah kategori yang dikelola",
      },
      {
        label: "Risiko Cakupan",
        value: 12,
        tone: "warning",
        helperText: "Baris provinsi dengan catatan kehati-hatian",
      },
    ],
    trend: createTrend(43),
    rankings: [
      { name: "Papua", region: "Provinsi", value: 97, quality: "Complete" },
      { name: "Nusa Tenggara Timur", region: "Provinsi", value: 88, quality: "Partial" },
      { name: "Aceh", region: "Provinsi", value: 82, quality: "Complete" },
    ],
    alerts: [
      {
        title: "Cakupan regional telah dinormalisasi",
        body: "Konsistensi kode wilayah siap untuk penyajian peta.",
        severity: "Low",
        module: "TKD",
      },
    ],
    advisor: {
      headline: "Kinerja transfer menunjukkan penguatan terfokus secara regional.",
      overview:
        "Garis tren mock mengindikasikan pergerakan yang lebih kuat di wilayah timur dibanding rata-rata nasional.",
      bullets: [
        "Tampilkan peringkat provinsi berdampingan dengan choropleth.",
        "Jadikan jenis TKD sebagai filter utama.",
        "Gunakan payload peta untuk satu metrik dalam satu waktu.",
      ],
      caution: "Perbandingan regional pada fondasi ini masih bersifat mock.",
    },
    filters: ["Tahun", "Provinsi", "Kabupaten/Kota", "Jenis TKD", "Regional"],
    endpoints: [
      "/api/v1/dashboard/tkd/overview",
      "/api/v1/dashboard/tkd/trends",
      "/api/v1/dashboard/tkd/map",
    ],
  },
  "dana-desa": {
    overview: "Realisasi Dana Desa bulanan dengan konteks pagu tahunan dan jangkauan desa.",
    metrics: [
      {
        label: "Realisasi Bulanan",
        value: 124.8,
        unit: "B",
        delta: 7.5,
        tone: "fiscal",
        helperText: "Snapshot bulan terbaru",
      },
      {
        label: "Cakupan Desa",
        value: 68240,
        delta: 2.1,
        tone: "success",
        helperText: "Jumlah desa penerima manfaat",
      },
      {
        label: "Konteks Pagu",
        value: 871.2,
        unit: "B",
        delta: 0.6,
        tone: "intelligence",
        helperText: "Konteks pagu tahunan",
      },
      {
        label: "Peringatan Cakupan",
        value: 9,
        tone: "warning",
        helperText: "Baris yang memerlukan label catatan",
      },
    ],
    trend: createTrend(61),
    rankings: [
      { name: "Kab. Bogor", region: "Kabupaten", value: 96, quality: "Complete" },
      { name: "Kab. Banyuwangi", region: "Kabupaten", value: 89, quality: "Complete" },
      { name: "Kab. Jayapura", region: "Kabupaten", value: 78, quality: "Partial" },
    ],
    alerts: [
      {
        title: "Grain bulanan dikunci",
        body: "Pagu dan realisasi tetap dimodelkan terpisah sesuai desain.",
        severity: "Low",
        module: "Dana Desa",
      },
    ],
    advisor: {
      headline: "Realisasi bulanan berada di atas siklus sebelumnya pada sinyal mock.",
      overview:
        "Fondasi saat ini memisahkan konteks pagu tahunan dari pergerakan bulanan untuk menjaga kejernihan analitis.",
      bullets: [
        "Pertahankan tren bulanan sebagai visual utama.",
        "Gunakan ringkasan tahunan untuk membandingkan pagu versus realisasi.",
        "Tampilkan cakupan desa penerima manfaat pada halaman yang sama.",
      ],
      caution: "Belum ada data Turso live yang dipanggil pada fondasi frontend ini.",
    },
    filters: ["Tahun", "Bulan", "Provinsi", "Kabupaten/Kota", "Regional"],
    endpoints: [
      "/api/v1/dashboard/dana-desa/overview",
      "/api/v1/dashboard/dana-desa/budget-vs-realization",
      "/api/v1/dashboard/dana-desa/map",
    ],
  },
  apbn: {
    overview: "Eksekusi anggaran kelembagaan dengan lensa BA, KPPN, kanwil, dan satker.",
    metrics: [
      {
        label: "Total Pagu",
        value: 5320,
        unit: "B",
        delta: 2.9,
        tone: "fiscal",
        helperText: "Konteks anggaran kelembagaan",
      },
      {
        label: "Total Realisasi",
        value: 4014,
        unit: "B",
        delta: 1.7,
        tone: "success",
        helperText: "Tren eksekusi",
      },
      {
        label: "Baris Bertanda",
        value: 55,
        tone: "risk",
        helperText: "Referensi mock yang belum terselesaikan",
      },
      {
        label: "Cakupan BA",
        value: 92,
        unit: "ref",
        tone: "warning",
        helperText: "Jumlah referensi master",
      },
    ],
    trend: createTrend(72),
    rankings: [
      { name: "Kementerian Keuangan", region: "BA", value: 98, quality: "Complete" },
      { name: "Kementerian PUPR", region: "BA", value: 87, quality: "Complete" },
      { name: "BRIN", region: "BA", value: 69, quality: "Flagged" },
    ],
    alerts: [
      {
        title: "Catatan referensi BA aktif",
        body: "Cakupan master masih perlu indikator tata kelola yang terlihat jelas.",
        severity: "High",
        module: "APBN",
      },
    ],
    advisor: {
      headline: "APBN sudah bisa dianalisis, tetapi kualitas referensi harus tetap eksplisit.",
      overview:
        "Pola kelembagaan cukup kuat untuk dashboard, namun UI tidak boleh menyembunyikan sinyal referensi bermasalah.",
      bullets: [
        "Jadikan banner kualitas selalu terlihat di halaman APBN.",
        "Gunakan peringkat BA dan kanwil sebagai tampilan awal.",
        "Pertahankan detail satker dalam tabel ringkas dengan badge per baris.",
      ],
      caution: "Kualitas referensi yang ditampilkan di sini masih bersifat ilustratif.",
    },
    filters: ["Tahun", "BA", "Kanwil", "KPPN", "Satker", "Jenis Belanja"],
    endpoints: [
      "/api/v1/dashboard/apbn/overview",
      "/api/v1/dashboard/apbn/by-ba",
      "/api/v1/dashboard/apbn/quality",
    ],
  },
  ikpa: {
    overview: "Pelacakan kinerja operasional bulanan untuk satker, KPPN, dan kanwil.",
    metrics: [
      {
        label: "Rata-rata IKPA",
        value: 91.2,
        unit: "%",
        delta: 1.8,
        tone: "success",
        helperText: "Skor rata-rata mock",
      },
      {
        label: "Performa Rendah",
        value: 18,
        tone: "risk",
        helperText: "Satker yang perlu ditinjau",
      },
      {
        label: "KPPN Aktif",
        value: 178,
        tone: "intelligence",
        helperText: "Anggota referensi aktif saat ini",
      },
      {
        label: "Bulan Tersedia",
        value: 6,
        unit: "periode",
        tone: "fiscal",
        helperText: "Periode mock yang dimuat",
      },
    ],
    trend: createTrend(75),
    rankings: [
      { name: "KPPN Jakarta II", region: "KPPN", value: 95, quality: "Complete" },
      { name: "KPPN Surabaya I", region: "KPPN", value: 92, quality: "Complete" },
      { name: "KPPN Sungai Penuh", region: "KPPN", value: 71, quality: "Flagged" },
    ],
    alerts: [
      {
        title: "Keterlambatan operasional terdeteksi",
        body: "Jumlah satker berkinerja rendah secara mock meningkat pada bulan berjalan.",
        severity: "Medium",
        module: "IKPA",
      },
    ],
    advisor: {
      headline: "Kesehatan IKPA tetap positif secara umum dengan friksi lokal.",
      overview:
        "Perbedaan level KPPN pada data mock lebih tajam dibanding level kanwil, sehingga drilldown menjadi bernilai.",
      bullets: [
        "Mulai dari rata-rata kanwil, lalu drill ke KPPN.",
        "Tampilkan tabel performa rendah di atas grafik yang padat.",
        "Pertahankan flag kualitas saat referensi satker belum lengkap.",
      ],
      caution: "Profil kinerja saat ini masih sepenuhnya mock.",
    },
    filters: ["Tahun", "Bulan", "Kanwil", "KPPN", "Satker"],
    endpoints: [
      "/api/v1/dashboard/ikpa/overview",
      "/api/v1/dashboard/ikpa/by-kppn",
      "/api/v1/dashboard/ikpa/by-satker",
    ],
  },
  kur: {
    overview:
      "Analisis dukungan KUR level provinsi dan kabupaten/kota dengan konteks subsektor.",
    metrics: [
      {
        label: "Total Subsidi",
        value: 348.5,
        unit: "B",
        delta: 4.5,
        tone: "fiscal",
        helperText: "Posisi subsidi mock",
      },
      {
        label: "Total Penerima",
        value: 182400,
        delta: 5.4,
        tone: "success",
        helperText: "Jumlah penerima mock",
      },
      {
        label: "Subsektor Aktif",
        value: 11,
        tone: "intelligence",
        helperText: "Subsektor mock yang aktif",
      },
      {
        label: "Catatan Cakupan",
        value: 4,
        tone: "warning",
        helperText: "Baris provinsi yang memerlukan catatan",
      },
    ],
    trend: createTrend(48),
    rankings: [
      { name: "Jawa Tengah", region: "Provinsi", value: 93, quality: "Complete" },
      { name: "Sulawesi Selatan", region: "Provinsi", value: 85, quality: "Complete" },
      { name: "Maluku", region: "Provinsi", value: 68, quality: "Partial" },
    ],
    alerts: [
      {
        title: "Catatan cakupan provinsi",
        body: "KUR level provinsi harus tetap diberi label eksplisit saat cakupan parsial.",
        severity: "Medium",
        module: "KUR",
      },
    ],
    advisor: {
      headline: "Pertumbuhan KUR meluas tetapi belum merata antar subsektor.",
      overview:
        "Fondasi ini diatur untuk perbandingan provinsi terlebih dahulu, lalu drilldown ke level kabupaten.",
      bullets: [
        "Tempatkan komposisi subsektor dekat dengan tren utama.",
        "Pisahkan interpretasi provinsi dan kabupaten.",
        "Gunakan badge kehati-hatian saat cakupan belum lengkap.",
      ],
      caution: "Metrik KUR saat ini dirender dari fixture mock.",
    },
    filters: ["Tahun", "Bulan", "Provinsi", "Kabupaten/Kota", "Subsektor"],
    endpoints: [
      "/api/v1/dashboard/kur/overview",
      "/api/v1/dashboard/kur/provinsi",
      "/api/v1/dashboard/kur/kabkota",
    ],
  },
  macro: {
    overview:
      "Sinyal makro level provinsi yang diselaraskan ke dalam satu permukaan perbandingan pembangunan.",
    metrics: [
      {
        label: "Sinyal IPM",
        value: 82.4,
        unit: "Indeks",
        delta: 1.9,
        tone: "success",
        helperText: "Sinyal kesejahteraan komposit",
      },
      {
        label: "Pertumbuhan PDRB",
        value: 5.7,
        unit: "%",
        delta: 0.8,
        tone: "fiscal",
        helperText: "Pertumbuhan tahunan mock",
      },
      {
        label: "Tekanan Kemiskinan",
        value: 9.4,
        unit: "%",
        delta: -0.6,
        tone: "warning",
        helperText: "Tingkat kemiskinan mock",
      },
      {
        label: "Risiko Tenaga Kerja",
        value: 5.2,
        unit: "%",
        delta: -0.3,
        tone: "intelligence",
        helperText: "Sinyal pengangguran mock",
      },
    ],
    trend: createTrend(59),
    rankings: [
      { name: "DKI Jakarta", region: "Provinsi", value: 97, quality: "Complete" },
      { name: "D.I. Yogyakarta", region: "Provinsi", value: 94, quality: "Complete" },
      { name: "Papua", region: "Provinsi", value: 70, quality: "Partial" },
    ],
    alerts: [
      {
        title: "Cakupan waktu makro berbeda antar dataset",
        body: "Label tahun harus tetap sesuai dengan dataset pada tampilan gabungan.",
        severity: "Medium",
        module: "Makro",
      },
    ],
    advisor: {
      headline: "Indikator makro menunjukkan ketahanan yang membaik dengan titik tekanan yang belum merata.",
      overview:
        "Profil provinsi perlu menggabungkan metrik pembangunan dan tekanan sosial tanpa menyembunyikan catatan cakupan waktu.",
      bullets: [
        "Utamakan satu kartu profil provinsi ditambah peringkat pembanding.",
        "Pertahankan label periode tahunan secara eksplisit.",
        "Gunakan salinan kualitas saat satu dataset makro lebih dangkal dari yang lain.",
      ],
      caution: "Indikator makro pada fondasi ini masih berupa nilai mock.",
    },
    filters: ["Tahun", "Provinsi", "Indikator"],
    endpoints: [
      "/api/v1/dashboard/macro/overview",
      "/api/v1/dashboard/macro/profile",
      "/api/v1/dashboard/macro/pdrb",
    ],
  },
  media: {
    overview: "Visibilitas narasi dan isu yang disejajarkan dengan intelijen regional dan fiskal.",
    metrics: [
      {
        label: "Topik Dipantau",
        value: 18,
        tone: "intelligence",
        helperText: "Tema media mock",
      },
      {
        label: "Artikel Terkini",
        value: 264,
        delta: 12.4,
        tone: "fiscal",
        helperText: "Kumpulan artikel mock bergulir",
      },
      {
        label: "Pantauan Sentimen",
        value: 7,
        tone: "warning",
        helperText: "Topik yang perlu tindak lanjut",
      },
      {
        label: "Analisis Tersimpan",
        value: 39,
        tone: "success",
        helperText: "Ringkasan media yang dapat digunakan ulang",
      },
    ],
    trend: createTrend(33),
    rankings: [
      { name: "Ketahanan Pangan", region: "Topik", value: 84, quality: "Complete" },
      { name: "Dana Desa", region: "Topik", value: 76, quality: "Complete" },
      { name: "Reformasi Kelembagaan", region: "Topik", value: 64, quality: "Partial" },
    ],
    alerts: [
      {
        title: "Penyegaran narasi disarankan",
        body: "Ambang kesegaran mock hampir tercapai pada satu klaster topik.",
        severity: "Low",
        module: "Media",
      },
    ],
    advisor: {
      headline: "Volume narasi stabil dengan beberapa tema berperhatian tinggi.",
      overview:
        "Media harus berperan sebagai intelijen kontekstual, bukan sumber kebenaran yang bersaing.",
      bullets: [
        "Pertahankan jumlah sumber dan tingkat kesegaran tetap terlihat.",
        "Sorot satu linimasa narasi dalam satu waktu.",
        "Hubungkan analisis topik kembali ke modul dashboard.",
      ],
      caution: "Panel berita masih placeholder yang disiapkan untuk endpoint Workers di masa depan.",
    },
    filters: ["Tahun", "Wilayah", "Topik", "Kata Kunci", "Rentang Tanggal"],
    endpoints: [
      "/api/v1/news/search",
      "/api/v1/news/timeline",
      "/api/v1/news/analysis",
    ],
  },
  ai: {
    overview:
      "Ringkasan strategis berbasis peran dan prompt perbandingan yang dibangun di atas konteks analitis terstruktur.",
    metrics: [
      {
        label: "Permintaan Advisor",
        value: 126,
        delta: 9.8,
        tone: "intelligence",
        helperText: "Penggunaan bulanan mock",
      },
      {
        label: "Brief Dihasilkan",
        value: 38,
        delta: 2.1,
        tone: "fiscal",
        helperText: "Output eksekutif mock",
      },
      {
        label: "Rasio Cache Hit",
        value: 71.4,
        unit: "%",
        delta: 6.7,
        tone: "success",
        helperText: "Disiapkan untuk cache AI di tahap berikutnya",
      },
      {
        label: "Peristiwa Catatan",
        value: 14,
        tone: "warning",
        helperText: "Respons dengan peringatan data eksplisit",
      },
    ],
    trend: createTrend(46),
    rankings: [
      { name: "Ringkasan Eksekutif", region: "Fitur", value: 92, quality: "Complete" },
      { name: "Brief Provinsi", region: "Fitur", value: 85, quality: "Complete" },
      { name: "Perbandingan Antar Fakta", region: "Fitur", value: 63, quality: "Flagged" },
    ],
    alerts: [
      {
        title: "Kontrak prompt contoh aktif",
        body: "Panel advisor siap menerima respons API yang terstruktur.",
        severity: "Low",
        module: "AI",
      },
    ],
    advisor: {
      headline: "Ruang kerja AI siap untuk dukungan keputusan yang terstruktur.",
      overview:
        "Fondasi ini menekankan pengungkapan sumber, prompt yang terikat, dan presentasi yang sadar kualitas.",
      bullets: [
        "Mulai dengan jawaban eksekutif yang singkat.",
        "Tampilkan modul sumber dan rentang waktu di bawah setiap respons.",
        "Teruskan metadata kualitas dan cakupan langsung ke panel.",
      ],
      caution: "Belum ada pemanggilan model pada scaffold frontend ini.",
    },
    filters: ["Fitur", "Modul", "Tahun", "Geografi", "Kanwil", "Satker"],
    endpoints: [
      "/api/v1/ai/summary",
      "/api/v1/ai/compare",
      "/api/v1/ai/briefsheet",
    ],
  },
  governance: {
    overview: "Bidang kendali operasional untuk kualitas data, auditabilitas AI, dan kesiapan cache.",
    metrics: [
      {
        label: "Isu Terbuka",
        value: 1118,
        delta: -4.4,
        tone: "risk",
        helperText: "Baris audit mock yang belum selesai",
      },
      {
        label: "Kesenjangan Master",
        value: 27,
        tone: "warning",
        helperText: "Pengecualian referensi yang berbeda",
      },
      {
        label: "Peristiwa Audit AI",
        value: 312,
        tone: "intelligence",
        helperText: "Interaksi mock yang tercatat",
      },
      {
        label: "Pipeline Sehat",
        value: 9,
        tone: "success",
        helperText: "Pipeline dalam ambang sehat",
      },
    ],
    trend: createTrend(28),
    rankings: [
      { name: "master_satker.csv", region: "Dataset", value: 87, quality: "Flagged" },
      { name: "master_wilayah.csv", region: "Dataset", value: 96, quality: "Complete" },
      { name: "data_ikpa_satker.csv", region: "Dataset", value: 81, quality: "Partial" },
    ],
    alerts: [
      {
        title: "Audit log memerlukan kebijakan deduplikasi",
        body: "Eksekusi berulang harus dapat dibedakan dari isu yang benar-benar baru.",
        severity: "High",
        module: "Tata Kelola",
      },
    ],
    advisor: {
      headline: "Posisi tata kelola stabil tetapi belum tenang.",
      overview:
        "Tim operasional sebaiknya memprioritaskan kelengkapan referensi dan kebersihan audit log sebelum memperluas otomatisasi berisiko lebih tinggi.",
      bullets: [
        "Pertahankan tren tingkat keparahan isu di atas tabel.",
        "Tampilkan pintasan audit referensi master.",
        "Perlakukan tata kelola sebagai halaman yang selalu aktif, bukan admin tersembunyi.",
      ],
      caution: "Jumlah yang ditampilkan adalah nilai mock yang diselaraskan dengan arsitektur terdokumentasi.",
    },
    filters: ["Dataset", "Severity", "Jenis Isu", "Resolved", "Fitur"],
    endpoints: [
      "/api/v1/governance/overview",
      "/api/v1/governance/data-quality",
      "/api/v1/governance/master-reference",
    ],
  },
};

async function mockFetchModuleDataset(moduleId: string): Promise<ModuleDataset> {
  const dataset = datasets[moduleId];
  if (!dataset) {
    throw new Error(`Dataset modul tidak dikenal: ${moduleId}`);
  }

  return dataset;
}

let fetchModuleDatasetImpl: (moduleId: string) => Promise<ModuleDataset> = mockFetchModuleDataset;

export function setFetchModuleDatasetImpl(
  impl: (moduleId: string) => Promise<ModuleDataset>,
): void {
  fetchModuleDatasetImpl = impl;
}

export async function fetchModuleDataset(moduleId: string): Promise<ModuleDataset> {
  return fetchModuleDatasetImpl(moduleId);
}

export async function fetchNavigationSummary(): Promise<{
  activeRole: string;
  environment: string;
  cacheStatus: string;
}> {
  return {
    activeRole: "Tampilan Eksekutif + Analis",
    environment: "Pratinjau Mock Siap Cloudflare",
    cacheStatus: "Strategi KV telah disiapkan",
  };
}
